import {skipToken} from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import {memo, useCallback, useEffect, useRef, useMemo, useState} from 'react'

import {useHeightResizeObserver} from '../../../../../../../hooks/useResizeObserver'
import type {
  TestOccurrence,
  GetAllTestOccurrencesNormalizedApiArg,
} from '../../../../../../../services/rest'
import {restApi} from '../../../../../../../services/rest'
import {stringifyId} from '../../../../../../../types'
import type {BuildId, TestOccurrenceId} from '../../../../../../../types'
import {scrollIntoViewIfNeeded} from '../../../../../../../utils/dom'
import {emptyArray, noop} from '../../../../../../../utils/empty'
import type {WritableKeyValue} from '../../../../../../../utils/object'
import Details from '../../../../../../common/Details/Details'
import RowGroup from '../../../../../../common/Grid/RowGroup'
import SvgIcon from '../../../../../../common/SvgIcon/SvgIcon'
import TestInvestigationHistoryPopup from '../../../../../../packages/InvestigationHistory/InvestigationHistoryPopup/TestInvestigationHistoryPopup.container'
import TestDetails from '../../../../../../packages/Tests/TestDetails/TestDetails'
import {
  getTestOccurrence,
  makeGetTestOccurrenceInvocations,
} from '../../../../../../packages/Tests/Tests.selectors'
import {TestStatus} from '../../../../../../packages/Tests/Tests.types'
import {getTestOccurrenceElementId} from '../../../../../../packages/Tests/Tests.utils'

import BuildTestItemAdvanced from './TestItemAdvanced/TestItemAdvanced'

import styles from './TestItem.css'

import {useAppSelector} from 'src/hooks/react-redux'

type BaseOwnProps = {
  buildId: BuildId
  testOccurrenceId?: TestOccurrenceId
  toggleSelectedTests: (id: TestOccurrenceId) => unknown
  toggleExpandedTest: (id?: TestOccurrenceId | null | undefined) => unknown
  withCheckbox?: boolean
}
export type OwnProps = BaseOwnProps & {
  expandedTest: TestOccurrenceId | null | undefined
  selectedTests: ReadonlyArray<TestOccurrenceId>
  invocationsArg?: GetAllTestOccurrencesNormalizedApiArg
}
type OwnPropsItem = BaseOwnProps & {
  groupedTest?: boolean
  invocation?: boolean
  expanded?: boolean
  checked?: boolean
}

function TestItem({
  buildId,
  testOccurrenceId,
  expanded = false,
  checked = false,
  withCheckbox,
  toggleSelectedTests = noop,
  toggleExpandedTest = noop,
  groupedTest = false,
  invocation = false,
}: OwnPropsItem) {
  const ref = useRef<HTMLDivElement>(null)
  const [innerHeight, measureRef] = useHeightResizeObserver()

  const testOccurrence = useAppSelector(state => getTestOccurrence(state, testOccurrenceId))
  const {name, status, build, metadata} = testOccurrence ?? {}

  const expandable =
    groupedTest ||
    status === TestStatus.FAILURE ||
    Boolean(metadata?.count) ||
    (!invocation && buildId !== build?.id)

  useEffect(() => {
    if (expanded && ref.current != null) {
      scrollIntoViewIfNeeded(ref.current)
    } // Only needed on first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleExpand = useCallback(() => {
    if (expandable && testOccurrenceId != null) {
      toggleExpandedTest(testOccurrenceId)
    }
  }, [expandable, toggleExpandedTest, testOccurrenceId])

  const renderRow = useMemo(
    () => (
      <>
        {groupedTest && (
          <SvgIcon
            className={styles.groupedTestArrow}
            icon={expanded ? 'chevron-up' : 'chevron-down'}
          />
        )}
        <BuildTestItemAdvanced
          className={classNames({[styles.expandable]: expandable})}
          buildId={buildId}
          testOccurrenceId={testOccurrenceId}
          expanded={expanded}
          checked={checked}
          invocation={invocation}
          withCheckbox={withCheckbox}
          onToggleSelect={toggleSelectedTests}
        />
        {!groupedTest && expandable && (
          <SvgIcon className={styles.arrow} icon={expanded ? 'chevron-up' : 'chevron-down'} />
        )}
      </>
    ),
    [
      buildId,
      checked,
      expandable,
      expanded,
      groupedTest,
      invocation,
      testOccurrenceId,
      toggleSelectedTests,
      withCheckbox,
    ],
  )
  return (
    <RowGroup span={2}>
      <div
        ref={ref}
        id={getTestOccurrenceElementId(testOccurrenceId)}
        className={classNames(styles.item, {
          [styles.groupedTest]: groupedTest,
          [styles.open]: expanded,
          [styles.closed]: !expanded,
        })}
        data-test-build-test-item={'details'}
      >
        <Details
          headingClassName={classNames(styles.heading, {[styles.expandable]: expandable})}
          level={2}
          label={name ?? 'Test'}
          open={!groupedTest && expanded}
          summary={renderRow}
          summaryClassName={styles.button}
          detailsWrapperClassName={styles.detailsWrapper}
          onToggle={toggleExpand}
          imitateHeading
          renderButtonAside
        >
          <div style={{height: innerHeight}} className={styles.outerContainer}>
            <div ref={measureRef} className={styles.innerContainer}>
              <TestDetails
                className={classNames({[styles.detailsShift]: invocation})}
                testOccurrenceId={testOccurrenceId}
                buildId={buildId}
              />
            </div>
          </div>
        </Details>
        <TestInvestigationHistoryPopup testOccurrenceId={testOccurrenceId} />
      </div>
    </RowGroup>
  )
}

function BuildTestWrapper(props: OwnProps) {
  const {testOccurrenceId} = props
  const {selectedTests, expandedTest, invocationsArg, ...otherProps} = props
  const {invocationIds} = restApi.endpoints.getAllTestOccurrencesNormalized.useQuery(
    invocationsArg ?? skipToken,
    {
      selectFromResult: ({data}) => ({
        invocationIds:
          data?.data.entities.testOccurrencesInvocations?.[testOccurrenceId!]?.invocations
            ?.testOccurrence ?? emptyArray,
      }),
    },
  )
  const getTestOccurrenceInvocations = useMemo(makeGetTestOccurrenceInvocations, [])
  const invocations = useAppSelector(state => getTestOccurrenceInvocations(state, invocationIds))

  const [filteredInvocations, expandedInvocation] = useMemo(() => {
    const hash: WritableKeyValue<BuildId, TestOccurrence> = {}
    const filteredArray: Array<TestOccurrence> = []
    let hasExpanded = false

    invocations.forEach(invocation => {
      const invocationId = invocation.id
      const invocationBuildId = invocation?.build?.id

      if (expandedTest === invocationId) {
        hasExpanded = true
      }
      if (invocationBuildId != null && hash[invocationBuildId] == null) {
        hash[invocationBuildId] = invocation
        filteredArray.push(invocation)
      }
    })
    if (Object.keys(hash).length > 1) {
      return [invocations, hasExpanded]
    }
    return [filteredArray, hasExpanded]
  }, [expandedTest, invocations])

  const groupedTest = filteredInvocations.length > 1

  const [expandedGroupedTest, setExpandedGroupedTest] = useState(expandedInvocation)
  const toggleExpandedGrouedTest = useCallback(
    () => setExpandedGroupedTest(!expandedGroupedTest),
    [expandedGroupedTest],
  )

  return (
    <>
      <TestItem
        {...otherProps}
        checked={selectedTests.includes(testOccurrenceId!)}
        expanded={groupedTest ? expandedGroupedTest : expandedTest === testOccurrenceId}
        toggleExpandedTest={groupedTest ? toggleExpandedGrouedTest : props.toggleExpandedTest}
        groupedTest={groupedTest}
      />
      {groupedTest && (expandedGroupedTest || expandedInvocation)
        ? filteredInvocations.map(({id}) => (
            <TestItem
              key={stringifyId(id)}
              testOccurrenceId={id}
              checked={selectedTests.includes(id!)}
              expanded={expandedTest === id}
              buildId={props.buildId}
              toggleSelectedTests={props.toggleSelectedTests}
              toggleExpandedTest={props.toggleExpandedTest}
              withCheckbox={false}
              invocation
            />
          ))
        : null}
    </>
  )
}

export default memo<OwnProps>(BuildTestWrapper)
